// =============================================================================
// useBackendImprovementStatus — sygnały "zrobione" dla kropek fazy Ulepszanie zaplecza.
//
// Bez tego Podstrony / Autor info / Logo i favicon nigdy nie zapalaly sie na zielono
// (brak wpisow w completedSteps) — licznik fazy stal na 0/4 mimo wykonanej roboty.
//
// 4 lekkie zapytania, odpalane TYLKO gdy faza jest widoczna. Odswiezaja sie przy
// zmianie kroku, wiec po zapisaniu logo/zdjec wynik aktualizuje sie od razu.
// =============================================================================

import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { listAuthorPhotos } from "@/services/uniquePages/authorPhotos";

export interface BackendImprovementStatus {
  uniquePagesReady: number;
  uniquePagesTotal: number;
  authorPhotos: number;
  personaFilled: boolean;
  hasLogo: boolean;
}

const EMPTY: BackendImprovementStatus = {
  uniquePagesReady: 0,
  uniquePagesTotal: 0,
  authorPhotos: 0,
  personaFilled: false,
  hasLogo: false,
};

export function useBackendImprovementStatus(
  mapId: string | undefined,
  enabled: boolean,
  /** Zmiana tej wartosci wymusza odswiezenie (podajemy activeStep). */
  refreshKey?: string,
) {
  const [status, setStatus] = useState<BackendImprovementStatus>(EMPTY);

  const load = useCallback(async () => {
    if (!mapId || !enabled) { setStatus(EMPTY); return; }

    const [subpages, persona, brand, photos] = await Promise.allSettled([
      (supabase as any).from("unique_subpages").select("status").eq("map_id", mapId),
      (supabase as any).from("map_persona").select("full_name, social_links, notes").eq("map_id", mapId).maybeSingle(),
      (supabase as any).from("map_brand_assets").select("logo_svg").eq("map_id", mapId).maybeSingle(),
      listAuthorPhotos(mapId),
    ]);

    const rows: { status?: string }[] =
      subpages.status === "fulfilled" ? (subpages.value?.data ?? []) : [];
    const personaRow = persona.status === "fulfilled" ? persona.value?.data : null;
    const brandRow = brand.status === "fulfilled" ? brand.value?.data : null;

    setStatus({
      uniquePagesTotal: rows.length,
      uniquePagesReady: rows.filter((r) => r.status === "ready" || r.status === "published").length,
      authorPhotos: photos.status === "fulfilled" ? photos.value.length : 0,
      personaFilled: Boolean(
        personaRow &&
          (personaRow.full_name ||
            personaRow.notes ||
            (Array.isArray(personaRow.social_links) && personaRow.social_links.length > 0)),
      ),
      hasLogo: Boolean(brandRow?.logo_svg),
    });
  }, [mapId, enabled]);

  useEffect(() => { void load(); }, [load, refreshKey]);

  return { status, refresh: load };
}
